/**
 * Firebase URL derivation utilities
 *
 * Extracts frontend URLs from Firebase configuration to ensure
 * invitation links and other redirects use the correct production URL
 * even when testing locally.
 */

import { ENV } from '../config/env';

/**
 * Validates and extracts project ID from Firebase auth domain
 * @param firebaseAuthDomain - Firebase auth domain (e.g., "docufy-459922.firebaseapp.com")
 * @returns Project ID if valid, null otherwise
 */
export function extractFirebaseProjectId(firebaseAuthDomain: string): string | null {
  if (!firebaseAuthDomain || typeof firebaseAuthDomain !== 'string') {
    return null;
  }

  // Validate Firebase auth domain format
  // Expected format: <project-id>.firebaseapp.com, <project-id>.web.app, or regional firebaseapp.com
  const firebaseDomainPattern = /^([a-z0-9-]+)(?:\.[a-z0-9-]+)?\.firebaseapp\.com$/i;
  const webAppDomainPattern = /^([a-z0-9-]+)\.web\.app$/i;
  const match =
    firebaseAuthDomain.match(firebaseDomainPattern) ||
    firebaseAuthDomain.match(webAppDomainPattern);

  if (!match) {
    return null;
  }

  // Extract project ID (first capture group)
  const projectId = match[1];

  // Validate project ID is not empty
  if (!projectId || projectId.trim().length === 0) {
    return null;
  }

  return projectId;
}

/**
 * Derives frontend URL from Firebase auth domain
 * Converts Firebase auth domain to Firebase hosting URL
 * (e.g., "docufy-459922.firebaseapp.com" -> "https://docufy-459922.web.app")
 *
 * @param firebaseAuthDomain - Optional Firebase auth domain. If not provided, reads from ENV
 * @returns Frontend URL if derivation succeeds, null otherwise
 */
export function deriveFrontendUrlFromFirebase(firebaseAuthDomain?: string): string | null {
  const domain = firebaseAuthDomain || ENV.VITE_FIREBASE_AUTH_DOMAIN || '';

  if (!domain) {
    return null;
  }

  // When auth domain is already the hosting URL, use it directly.
  if (/\.web\.app$/i.test(domain)) {
    return `https://${domain.replace(/^https?:\/\//i, '')}`;
  }

  const projectId = extractFirebaseProjectId(domain);
  if (!projectId) {
    return null;
  }

  // Construct Firebase hosting URL
  return `https://${projectId}.web.app`;
}

/**
 * Gets the frontend URL for invitation links
 * Uses Firebase auth domain to derive the production URL
 *
 * @returns Frontend URL if available, null otherwise
 */
export function getInvitationFrontendUrl(): string | null {
  return deriveFrontendUrlFromFirebase();
}
